// TanStack Query hooks for the company mailbox provisioning module.
// Spec: docs/superpowers/specs/2026-06-07-company-mailbox-provisioning-design.md
// HR surface: /api/persons/:id/mailbox[…]
// Polls every 10 s while a job is in flight (state === 'pending').

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MailboxStatusResponse, MailboxJobResponse, WebmailTokenResponse } from '@dsr/shared-types';
import { api } from '../client';

const key = (personId: number) => ['mailbox', personId] as const;

export function useMailboxStatus(personId: number) {
  return useQuery({
    queryKey: key(personId),
    queryFn: ({ signal }) =>
      api.get<MailboxStatusResponse>(`/api/persons/${personId}/mailbox`, signal),
    // Poll while a job is in flight. TanStack v5: callback receives the Query
    // object; access data via q.state.data.
    refetchInterval: (q) => (q.state.data?.state === 'pending' ? 10_000 : false),
  });
}

export function useProvisionMailbox(personId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { address?: string }) =>
      api.post<MailboxJobResponse>(`/api/persons/${personId}/mailbox`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(personId) }),
  });
}

export function useDisableMailbox(personId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<MailboxJobResponse>(`/api/persons/${personId}/mailbox/disable`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(personId) }),
  });
}

export function useResendMailboxCredentials(personId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: true }>(`/api/persons/${personId}/mailbox/resend-credentials`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(personId) }),
  });
}

export function useWebmailToken() {
  return useQuery({
    queryKey: ['mailbox-webmail-token'] as const,
    queryFn: ({ signal }) =>
      api.get<WebmailTokenResponse>('/api/me/mailbox/webmail-token', signal),
    gcTime: 0,          // clear cache on unmount — token is only valid 60 s
    staleTime: Infinity, // never refetch while mounted (would reload the iframe)
    retry: false,       // 404/409 = no mailbox — don't hammer
  });
}
